import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Get all unique field names from the shipments array
export const getAllAvailableFields = shipments => {
  if (!shipments || shipments.length === 0) return [];

  // Get all unique keys from all shipments
  const allKeys = new Set();
  shipments.forEach(shipment => {
    Object.keys(shipment).forEach(key => {
      // Skip internal/technical fields that start with underscore or are IDs
      if (!key.startsWith('_') && !key.toLowerCase().includes('password')) {
        allKeys.add(key);
      }
    });
  });

  // Convert to array and sort alphabetically
  return Array.from(allKeys).sort();
};

// Get field categories for better organization
export const getFieldCategories = fields => {
  const categories = {
    'Shipment Info': [],
    'Origin Info': [],
    'Destination Info': [],
    'Carrier Info': [],
    'Financial Info': [],
    Dates: [],
    Other: []
  };

  fields.forEach(field => {
    const lowerField = field.toLowerCase();

    if (lowerField.includes('origin') || lowerField.includes('pickup')) {
      categories['Origin Info'].push(field);
    } else if (
      lowerField.includes('destination') ||
      lowerField.includes('delivery')
    ) {
      categories['Destination Info'].push(field);
    } else if (
      lowerField.includes('carrier') ||
      lowerField.includes('scac') ||
      lowerField.includes('pro')
    ) {
      categories['Carrier Info'].push(field);
    } else if (
      lowerField.includes('charge') ||
      lowerField.includes('cost') ||
      lowerField.includes('rate') ||
      lowerField.includes('net') ||
      lowerField.includes('surcharge') ||
      lowerField.includes('freight')
    ) {
      categories['Financial Info'].push(field);
    } else if (lowerField.includes('date') || lowerField.includes('time')) {
      categories['Dates'].push(field);
    } else if (
      lowerField.includes('id') ||
      lowerField.includes('number') ||
      lowerField.includes('status') ||
      lowerField.includes('type') ||
      lowerField.includes('rule')
    ) {
      categories['Shipment Info'].push(field);
    } else {
      categories['Other'].push(field);
    }
  });

  // Remove empty categories
  Object.keys(categories).forEach(key => {
    if (categories[key].length === 0) {
      delete categories[key];
    }
  });

  return categories;
};

// Format field name for display
export const formatFieldName = fieldName => {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space between camelCase
};

// Flatten object for export
export const flattenObject = (obj, prefix = '') => {
  return Object.keys(obj).reduce((acc, key) => {
    const pre = prefix.length ? `${prefix}_` : '';
    if (
      typeof obj[key] === 'object' &&
      obj[key] !== null &&
      !Array.isArray(obj[key])
    ) {
      Object.assign(acc, flattenObject(obj[key], `${pre}${key}`));
    } else {
      acc[`${pre}${key}`] =
        obj[key] !== null && obj[key] !== undefined ? obj[key] : '';
    }
    return acc;
  }, {});
};

// Export to CSV
export const exportToCSV = (data, filename = 'export') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const flattenedData = data.map(item => flattenObject(item));
  const headers = [...new Set(flattenedData.flatMap(Object.keys))];

  const csvContent = [
    headers.join(','),
    ...flattenedData.map(row =>
      headers
        .map(header => {
          const value = row[header] || '';
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
};

// Export to Excel
export const exportToExcel = (
  data,
  filename = 'export',
  sheetName = 'Sheet1'
) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const flattenedData = data.map(item => flattenObject(item));
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(flattenedData);

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(
    wb,
    `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
};

// Export to JSON
export const exportToJSON = (data, filename = 'export') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.json`);
};

// Format currency
export const formatCurrency = value => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

// Format number
export const formatNumber = value => {
  return new Intl.NumberFormat('en-US').format(value || 0);
};

// Format date
export const formatDate = dateString => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};
